/* Maintenance — service intervals, compliance expiry, and burn drift.
 *
 * Same fd-screen shell as High Risk / Fuel Report. The page still does not
 * decide anything: engine/maintenance/predict scores each bus, this renders it.
 * Zana reads that same assessment, so the tab and her sentence cannot drift.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaArrowLeft, FaCheck, FaExclamationTriangle, FaGasPump, FaSearch, FaWrench,
} from "react-icons/fa";
import { auth } from "../firebase";
import { monogram } from "../fleet/alertMeta";
import { useLockBodyScroll } from "../fleet/useLockBodyScroll";

const BASE = (process.env.REACT_APP_PAYMENTS_URL || "").replace(/\/$/, "");

type ServiceState = "not_configured" | "overdue" | "due_soon" | "ok";
type DriftState = "no_baseline" | "insufficient_fills" | "ok" | "drifting";
type Kind =
  | "expired"
  | "overdue"
  | "expiring"
  | "due_soon"
  | "bad_reading"
  | "drifting"
  | "expired_ack"
  | "not_configured"
  | "ok";

interface Compliance {
  kind: string;
  label: string;
  state: "expired" | "expiring";
  daysAgo?: number;
  daysLeft?: number;
  /** The stored expiry instant. Its Nairobi calendar date is the date on the document. */
  atMs?: number;
  /** Expired and acknowledged: still not legal to run, just no longer nagging. */
  acknowledged?: boolean;
}

/** Present only while the acknowledgement holds (engine/maintenance/complianceAck). */
interface ComplianceAck {
  ackedAtMs: number;
  ackedByName: string | null;
  untilMs: number;
}

interface Vehicle {
  busId: string;
  docId: string | null;
  nickname: string | null;
  severity: "high" | "medium" | "ok";
  config: {
    serviceIntervalKm: number | null;
    lastServiceOdometerKm: number | null;
    insuranceExpiryMs: number | null;
    inspectionExpiryMs: number | null;
    odometerKm: number | null;
  };
  service: {
    state: ServiceState;
    kmSinceService?: number;
    remainingKm?: number;
    intervalKm?: number;
    odometerKm?: number;
    daysRemaining?: number | null;
    overdueKm?: number;
    missing?: string[];
  };
  compliance: Compliance[];
  complianceAck?: ComplianceAck | null;
  drift: {
    state: DriftState;
    driftPct?: number;
    recentKmPerL?: number;
    baselineKmPerL?: number;
    extraLitres30d?: number | null;
    estMonthlyLossKES?: number | null;
  };
}

interface Summary {
  assessed: number;
  overdue: number;
  dueSoon: number;
  notConfigured: number;
  expired: number;
  expiring: number;
  drifting: number;
  driftCostKESPerMonth: number;
  /** Of `expired`, how many are acknowledged — counted inside it, never subtracted. */
  expiredAcknowledged?: number;
}

interface Props {
  fleetOrgCode: string;
  onBack: () => void;
  onOpenBus?: (busId: string) => void;
}

const ksh = (n: number) => `Ksh ${Math.round(n).toLocaleString("en-US")}`;
const km = (n: number | null | undefined) =>
  n == null ? "—" : `${Math.round(n).toLocaleString("en-US")} km`;

/* Every date on this page is a Nairobi calendar date, whatever the browser's
   time zone (2026-09-14). The form's "2026-08-30" is stored as UTC midnight:
   03:00 on 30 Aug in Nairobi, but 20:00 on 29 Aug in New York, so a browser
   west of UTC showed, and compared against, the day before. Cover holds through
   its date in Nairobi (backend engine/maintenance/expiryDay). Kenya keeps no
   daylight saving, so UTC+3 is exact; read the shifted Date with getUTC* only. */
const NAIROBI_OFFSET_MS = 3 * 60 * 60 * 1000;

const toInputDate = (ms: number | null) => {
  if (!ms || !Number.isFinite(ms)) return "";
  return new Date(ms + NAIROBI_OFFSET_MS).toISOString().slice(0, 10);
};

/** Today on the Nairobi calendar as yyyy-mm-dd — what `<input type="date">` uses. */
const todayYmd = () => toInputDate(Date.now());

/** Nairobi calendar days past the date: expired cover is 1 (yesterday) at the least.
 *  0 can only come from a backend older than that rule. */
const agoLabel = (days: number | undefined) =>
  days === 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;

/** Nairobi calendar days to the date, and cover holds all of that day. */
const inLabel = (days: number | undefined) =>
  days === 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "14 Sep" on the Nairobi calendar. By hand, because en-GB now prints "14 Sept". */
const dayLabel = (ms: number) => {
  const d = new Date(ms + NAIROBI_OFFSET_MS);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

/** " · 30 Aug": the date on the document, beside the day count. */
const printedDate = (c: Compliance) => (c.atMs ? ` · ${dayLabel(c.atMs)}` : "");

type KmIssues = { serviceIntervalKm?: string; lastServiceOdometerKm?: string };

/* The km rules the save route enforces (engine/maintenance/vehicleConfig), said
   under the field before the round trip. There is no <form>, so min={1} never
   stopped a 0 — it was saved as "not set" and the field just vanished. */
function kmIssues(form: Record<string, string>, v: Vehicle): KmIssues {
  const out: KmIssues = {};
  const interval = (form.serviceIntervalKm ?? "").trim();
  if (interval !== "" && !(Number(interval) > 0)) {
    out.serviceIntervalKm = "Has to be more than 0 km — leave it empty if this bus has no schedule.";
  }
  const last = (form.lastServiceOdometerKm ?? "").trim();
  const odo = v.config.odometerKm;
  if (last !== "" && !(Number(last) > 0)) {
    out.lastServiceOdometerKm = "Has to be more than 0 km — leave it empty if you do not have the receipt.";
  } else if (last !== "" && odo != null && odo > 0 && Math.round(Number(last)) > Math.round(odo)) {
    // Whole km, like the backend: "Use latest dash reading" rounds, and must never trip this.
    out.lastServiceOdometerKm = `Higher than this bus's latest reading (${km(odo)}). Check the receipt for a typo.`;
  }
  return out;
}

const META: Record<Kind, { label: string; pill: string; ig: string; dot: string }> = {
  expired: { label: "Not legal to run", pill: "fd-pill-crit", ig: "fd-ig-crit", dot: "fd-dot-crit" },
  overdue: { label: "Service overdue", pill: "fd-pill-crit", ig: "fd-ig-crit", dot: "fd-dot-crit" },
  expiring: { label: "Cover expiring", pill: "fd-pill-warn", ig: "fd-ig-warn", dot: "fd-dot-warn" },
  due_soon: { label: "Service due soon", pill: "fd-pill-warn", ig: "fd-ig-warn", dot: "fd-dot-warn" },
  bad_reading: { label: "Check last service reading", pill: "fd-pill-warn", ig: "fd-ig-warn", dot: "fd-dot-warn" },
  drifting: { label: "Burning more fuel", pill: "fd-pill-info", ig: "fd-ig-brand", dot: "fd-dot-brand" },
  expired_ack: { label: "Not legal to run · acknowledged", pill: "fd-pill-mute", ig: "fd-ig-crit", dot: "" },
  not_configured: { label: "Not set up", pill: "fd-pill-mute", ig: "fd-ig-mute", dot: "" },
  ok: { label: "On schedule", pill: "fd-pill-ok", ig: "fd-ig-ok", dot: "fd-dot-ok" },
};

const ORDER: Kind[] = [
  "expired", "overdue", "expiring", "due_soon", "bad_reading", "drifting", "expired_ack", "not_configured", "ok",
];

function worst(v: Vehicle): Kind {
  const expired = v.compliance.filter((c) => c.state === "expired");
  if (expired.some((c) => !c.acknowledged)) return "expired";
  if (v.service.state === "overdue") return "overdue";
  if (v.compliance.some((c) => c.state === "expiring")) return "expiring";
  if (v.service.state === "due_soon") return "due_soon";
  if (v.service.missing?.includes("odometer_behind_last_service")) return "bad_reading";
  if (v.drift.state === "drifting") return "drifting";
  /* Acknowledged cover ranks below anything still unhandled, and above "not set
     up" and "on schedule": the bus is still not legal to run, and says so. */
  if (expired.length) return "expired_ack";
  if (v.service.state === "not_configured") return "not_configured";
  return "ok";
}

function needsLook(kind: Kind) {
  return kind !== "ok";
}

function kindIcon(kind: Kind) {
  if (kind === "expired" || kind === "expired_ack" || kind === "expiring") return <FaExclamationTriangle />;
  if (kind === "drifting") return <FaGasPump />;
  if (kind === "ok") return <FaCheck />;
  return <FaWrench />;
}

function displayName(v: Vehicle) {
  return v.nickname || v.busId;
}

function heroTitle(v: Vehicle, kind: Kind): string {
  if (kind === "expired" || kind === "expired_ack") {
    const acked = kind === "expired_ack";
    const c = v.compliance.find((x) => x.state === "expired" && (acked || !x.acknowledged));
    if (!c) return "Not legal to run";
    return `${c.label} expired ${agoLabel(c.daysAgo)}${acked ? " — acknowledged" : ""}`;
  }
  if (kind === "overdue") return `Service overdue by ${km(v.service.overdueKm)}`;
  if (kind === "expiring") {
    const c = v.compliance.find((x) => x.state === "expiring");
    return c ? `${c.label} expires ${inLabel(c.daysLeft)}` : "Cover expiring";
  }
  if (kind === "due_soon") {
    const days = v.service.daysRemaining != null ? ` (~${v.service.daysRemaining} days)` : "";
    return `Service due in ${km(v.service.remainingKm)}${days}`;
  }
  if (kind === "drifting") {
    return v.drift.driftPct != null
      ? `Burning ${v.drift.driftPct}% more fuel than its own baseline`
      : "Burning more fuel than its own baseline";
  }
  if (kind === "bad_reading") {
    return `Last service reading (${km(v.config.lastServiceOdometerKm)}) is higher than the odometer (${km(
      v.config.odometerKm,
    )}) — check for a typo`;
  }
  if (kind === "not_configured") {
    if (v.service.missing?.includes("odometer")) {
      return "No odometer yet — this bus has not logged a refill with a reading";
    }
    return "Set a service interval and last service reading to track this bus";
  }
  return "On schedule";
}

/* Only a positive km value pre-fills. A cleared field used to come back as 0
   (Number(null) in the entity layer), and the form then refused its own "0"
   under a field the owner never touched (2026-09-14). Anything not above 0 is
   "not set" here, so Save clears it instead of sending it back to be refused. */
const kmInput = (n: number | null) => (n != null && n > 0 ? String(n) : "");

/* The line under Save once the reload is back, read off the reloaded row, so the
   owner sees whether the renewal cleared the alert rather than guessing. */
function savedLine(v: Vehicle): string {
  if (v.compliance.some((c) => c.state === "expired")) return "Saved. Cover on file is still expired.";
  const bothDated = v.config.insuranceExpiryMs != null && v.config.inspectionExpiryMs != null;
  if (bothDated && v.compliance.length === 0) return "Saved — cover is up to date.";
  return "Saved.";
}

function formFrom(v: Vehicle): Record<string, string> {
  return {
    serviceIntervalKm: kmInput(v.config.serviceIntervalKm),
    lastServiceOdometerKm: kmInput(v.config.lastServiceOdometerKm),
    insuranceExpiry: toInputDate(v.config.insuranceExpiryMs),
    inspectionExpiry: toInputDate(v.config.inspectionExpiryMs),
  };
}

export default function MaintenancePage({ fleetOrgCode, onBack, onOpenBus }: Props) {
  useLockBodyScroll();
  const [rows, setRows] = useState<Vehicle[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"attention" | "setup" | "all">("attention");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /* The bus just saved, kept in the list whatever the filter. Renewing the only
     problem on a bus drops it out of "Needs a look", and the pane fell back to
     the first row: another bus, still expired, with that bus's dates in the form
     (2026-09-14). Let go on a chip or another row. */
  const [keptId, setKeptId] = useState<string | null>(null);
  const [acking, setAcking] = useState(false);
  const [ackMsg, setAckMsg] = useState<string | null>(null);
  const insuranceInputRef = useRef<HTMLInputElement>(null);
  const inspectionInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(
    async (refresh = false) => {
      if (!fleetOrgCode) return;
      setLoading(true);
      setErr(null);
      try {
        if (!BASE) {
          setErr("Payments API URL is not configured (REACT_APP_PAYMENTS_URL).");
          return;
        }
        const token = await auth.currentUser?.getIdToken();
        if (!token) {
          setErr("Sign in again to view maintenance.");
          return;
        }
        const res = await fetch(
          `${BASE}/api/premium/maintenance?orgCode=${encodeURIComponent(fleetOrgCode)}${
            refresh ? "&refresh=1" : ""
          }`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data = await res.json();
        if (!data.ok) {
          setErr(data.message || "Could not load maintenance.");
          return;
        }
        setRows(Array.isArray(data.maintenance) ? data.maintenance : []);
        setSummary(data.summary || null);
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Could not load maintenance.");
      } finally {
        setLoading(false);
      }
    },
    [fleetOrgCode],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const ordered = useMemo(
    () =>
      [...rows].sort(
        (a, b) => ORDER.indexOf(worst(a)) - ORDER.indexOf(worst(b)) || a.busId.localeCompare(b.busId),
      ),
    [rows],
  );

  const attentionCount = useMemo(
    () => rows.filter((v) => needsLook(worst(v))).length,
    [rows],
  );
  const setupCount = useMemo(
    () => rows.filter((v) => worst(v) === "not_configured").length,
    [rows],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ordered.filter((v) => {
      const kind = worst(v);
      const kept = keptId != null && (v.docId || v.busId) === keptId;
      if (filter === "attention" && !needsLook(kind) && !kept) return false;
      if (filter === "setup" && kind !== "not_configured" && !kept) return false;
      if (!q) return true;
      return (
        v.busId.toLowerCase().includes(q) ||
        (v.nickname || "").toLowerCase().includes(q)
      );
    });
  }, [ordered, filter, search, keptId]);

  const selected = useMemo(
    () => visible.find((v) => (v.docId || v.busId) === selectedId) ?? visible[0] ?? null,
    [visible, selectedId],
  );

  useEffect(() => {
    if (!selected) return;
    setForm(formFrom(selected));
    setMsg(null);
    setAckMsg(null);
    /* Keyed on the bus, not the row object: every reload (Refresh, Acknowledge,
       Undo) hands back a new object for the same bus, and re-seeding the form
       then would wipe dates the owner is halfway through typing. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.docId, selected?.busId]);

  const grouped = useMemo(() => {
    const out: { label: string; items: Vehicle[] }[] = [];
    for (const v of visible) {
      const label = META[worst(v)].label;
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(v);
      else out.push({ label, items: [v] });
    }
    return out;
  }, [visible]);

  const save = async (v: Vehicle) => {
    if (!v.docId) return;
    /* Stay on this bus. The pane shows the first row until one is clicked, and a
       save re-sorts the list, so it used to jump to whichever bus sorts first. */
    setSelectedId(v.docId);
    if (keptId !== v.docId) setKeptId(null);
    const issues = kmIssues(form, v);
    if (issues.serviceIntervalKm) {
      setMsg(`Service every (km): ${issues.serviceIntervalKm}`);
      return;
    }
    /* A reading above the odometer that is already on file is let through, as
       the backend does and as an expired date on file is: it is flagged on its
       own, and refusing it here would block saving a renewed insurance date on
       the same form. A reading of 0 or below never is — the backend refuses it. */
    const lastKm = Number((form.lastServiceOdometerKm ?? "").trim());
    const lastOnFile = lastKm > 0 && lastKm === v.config.lastServiceOdometerKm;
    if (issues.lastServiceOdometerKm && !lastOnFile) {
      setMsg(`Odometer at last service: ${issues.lastServiceOdometerKm}`);
      return;
    }
    const today = todayYmd();
    const expiryFields = [
      { k: "insuranceExpiry", label: "Insurance expiry", stored: toInputDate(v.config.insuranceExpiryMs) },
      { k: "inspectionExpiry", label: "NTSA inspection expiry", stored: toInputDate(v.config.inspectionExpiryMs) },
    ] as const;
    for (const f of expiryFields) {
      const next = (form[f.k] || "").trim();
      if (!next) continue;
      if (next < today && next !== f.stored) {
        setMsg(`${f.label} has to be today or later.`);
        return;
      }
    }
    /* A date the owner did not touch is left out of the body, and the route then
       leaves it alone. Sent back as "yyyy-mm-dd" it was stored as UTC midnight, so
       a date on file at Nairobi midnight (an import, a console Timestamp) moved
       instant on a save of the service interval alone, and the acknowledgement
       matching the old millis stopped holding (2026-09-14). */
    const expiryBody = (f: (typeof expiryFields)[number]) => {
      const next = form[f.k];
      if (next === f.stored) return undefined;
      return next === "" ? null : next;
    };
    setSaving(true);
    setMsg(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        setMsg("Sign in again to save.");
        return;
      }
      const res = await fetch(`${BASE}/api/premium/maintenance/vehicle`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          orgCode: fleetOrgCode,
          docId: v.docId,
          serviceIntervalKm: form.serviceIntervalKm === "" ? null : form.serviceIntervalKm,
          lastServiceOdometerKm:
            form.lastServiceOdometerKm === "" ? null : form.lastServiceOdometerKm,
          insuranceExpiry: expiryBody(expiryFields[0]),
          inspectionExpiry: expiryBody(expiryFields[1]),
        }),
      });
      const data = await res.json();
      if (!data.ok) {
        setMsg(data.message || "Could not save.");
        return;
      }
      // Before the reload, so the row that stops needing a look never leaves the list.
      setKeptId(v.docId);
      await load(true);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  /* Acknowledge "not legal to run", or Undo. The backend writes the record (same
     strict admin check as Save) and drops the cached read, so the reload shows it. */
  const setComplianceAck = async (v: Vehicle, undo: boolean) => {
    if (!v.docId) return;
    /* Pin the bus first. Acknowledging moves it down the list (expired →
       acknowledged), so the pane jumped to another bus and the owner never saw
       "acknowledged by …" or the Undo it came with (2026-09-14). */
    setSelectedId(v.docId);
    setAcking(true);
    setAckMsg(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        setAckMsg("Sign in again to save.");
        return;
      }
      const res = await fetch(`${BASE}/api/premium/maintenance/compliance-ack`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ orgCode: fleetOrgCode, docId: v.docId, undo }),
      });
      const data = await res.json();
      if (!data.ok) {
        setAckMsg(data.message || "Could not save.");
        return;
      }
      await load(true);
    } catch (e) {
      setAckMsg(e instanceof Error ? e.message : "Could not save.");
    } finally {
      setAcking(false);
    }
  };

  /* "Renewed? Update dates": the date fields sit up in Service schedule, above
     the Cover card. Land on the first document that needs renewing. */
  const focusExpiryFields = (v: Vehicle) => {
    const first = v.compliance.find((c) => c.state === "expired") ?? v.compliance[0];
    const el = first?.kind === "inspection" ? inspectionInputRef.current : insuranceInputRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
  };

  const kind = selected ? worst(selected) : "ok";
  const meta = META[kind];
  const kmIssue: KmIssues = selected ? kmIssues(form, selected) : {};
  const expiredAcked = summary?.expiredAcknowledged ?? 0;
  const nextService =
    !selected || selected.service.state === "not_configured"
      ? "—"
      : selected.service.state === "overdue"
        ? `${km(selected.service.overdueKm)} overdue`
        : `${km(selected.service.remainingKm)}${
            selected.service.daysRemaining != null ? ` (~${selected.service.daysRemaining}d)` : ""
          }`;

  return (
    <div className="fd-screen" data-zt="maintenancePage">
      <header className="fd-appbar">
        <button type="button" className="fd-iconbtn" onClick={onBack} aria-label="Back">
          <FaArrowLeft />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1>Maintenance</h1>
          <p className="fd-sub">
            {rows.length} bus{rows.length === 1 ? "" : "es"}
            {summary
              ? attentionCount > 0
                ? ` · ${attentionCount} need a look`
                : " · all on schedule"
              : ""}
          </p>
        </div>
        <button
          type="button"
          className="fd-btn fd-btn-quiet fd-btn-sm"
          onClick={() => void load(true)}
          disabled={loading}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      {err && (
        <div className="fd-note" style={{ margin: "12px 24px 0", background: "var(--fd-crit-050)", borderColor: "var(--fd-crit-200)", color: "var(--fd-crit-700)" }}>
          {err}
        </div>
      )}

      <section className="fd-strip" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
        <div className="fd-cell">
          <span className="fd-kicker">Not legal to run</span>
          {/* Acknowledged buses stay in the count (still not legal) and are named beside it. */}
          <div
            className="fd-big fd-num"
            style={summary && summary.expired > expiredAcked ? { color: "var(--fd-crit-600)" } : undefined}
          >
            {summary ? summary.expired : "—"}
            {expiredAcked > 0 ? <small>· {expiredAcked} acknowledged</small> : null}
          </div>
          <p className="fd-cellsub">
            {summary?.expiring
              ? `${summary.expiring} more expiring within 30 days`
              : "Insurance or NTSA inspection expired"}
          </p>
        </div>
        <div className="fd-cell">
          <span className="fd-kicker">Service overdue</span>
          <div className="fd-big fd-num" style={summary?.overdue ? { color: "var(--fd-crit-600)" } : undefined}>
            {summary ? summary.overdue : "—"}
          </div>
          <p className="fd-cellsub">
            {summary?.dueSoon ? `${summary.dueSoon} due soon` : "Past the interval you set"}
          </p>
        </div>
        <div className="fd-cell">
          <span className="fd-kicker">Burning more than normal</span>
          <div className="fd-big fd-num" style={summary?.drifting ? { color: "var(--fd-brand-700)" } : undefined}>
            {summary ? summary.drifting : "—"}
          </div>
          <p className="fd-cellsub">Worse than this bus’s own learned baseline</p>
        </div>
        <div className="fd-cell">
          <span className="fd-kicker">Drift cost / month</span>
          <div className="fd-big fd-num">
            {summary?.driftCostKESPerMonth ? Math.round(summary.driftCostKESPerMonth).toLocaleString() : "—"}
            {summary?.driftCostKESPerMonth ? <small>KES</small> : null}
          </div>
          <p className="fd-cellsub">Extra fuel from buses that are drifting</p>
        </div>
      </section>

      <div className="fd-split">
        <aside className="fd-rail">
          <div className="fd-railtop">
            <div className="fd-search">
              <FaSearch style={{ color: "var(--fd-ink-400)", flex: "0 0 auto" }} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search bus or plate"
                aria-label="Search buses"
              />
            </div>
            <div className="fd-chips">
              {([
                ["attention", `Needs a look · ${attentionCount}`],
                ["setup", `Not set up · ${setupCount}`],
                ["all", `All · ${rows.length}`],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className="fd-chip"
                  aria-pressed={filter === key}
                  onClick={() => { setFilter(key); setSelectedId(null); setKeptId(null); }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="fd-raillist">
            {loading && !rows.length && (
              <div className="fd-empty">
                <span>Loading…</span>
              </div>
            )}
            {!loading && visible.length === 0 && (
              <div className="fd-empty">
                <FaCheck size={26} />
                <b>{rows.length === 0 ? "No buses to assess" : "Nothing in this filter"}</b>
                <span>
                  {rows.length === 0
                    ? "Add a bus and its service standing will appear here."
                    : "Try All, or another chip."}
                </span>
              </div>
            )}
            {grouped.map((g) => (
              <div key={g.label}>
                <p className="fd-groupname">{g.label}</p>
                {g.items.map((v) => {
                  const k = worst(v);
                  const m = META[k];
                  const id = v.docId || v.busId;
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`fd-item${k === "ok" ? " fd-item-read" : ""}`}
                      aria-current={selected && (selected.docId || selected.busId) === id ? "true" : undefined}
                      onClick={() => { setSelectedId(id); if (id !== keptId) setKeptId(null); }}
                    >
                      <span className={`fd-ig ${m.ig}`}>{kindIcon(k)}</span>
                      <span className="fd-itxt">
                        <span className="fd-ititle" style={{ display: "block" }}>{displayName(v)}</span>
                        <span className="fd-imeta">
                          <span>{v.busId}</span>
                          <span className="fd-sep">·</span>
                          <span>{km(v.config.odometerKm)}</span>
                          {v.complianceAck && (
                            <>
                              <span className="fd-sep">·</span>
                              <span>Cover acknowledged</span>
                            </>
                          )}
                        </span>
                      </span>
                      {needsLook(k) && k !== "not_configured" && k !== "expired_ack" && <span className="fd-unread" />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </aside>

        <section className="fd-detail">
          {!selected && (
            <div className="fd-empty" style={{ paddingTop: 120 }}>
              <FaWrench size={30} />
              <b>Pick a bus</b>
              <span>Service, cover and fuel drift for that vehicle land here.</span>
            </div>
          )}

          {selected && (
            <>
              <div className="fd-hero">
                <div className="fd-heroinner">
                  <div style={{ minWidth: 0 }}>
                    <span className="fd-pill fd-pill-onbrand">{meta.label}</span>
                    <h2>{heroTitle(selected, kind)}</h2>
                    <div className="fd-herometa">
                      <span className="fd-avatar">{monogram(displayName(selected))}</span>
                      <span style={{ color: "#fff", fontWeight: 600 }}>{displayName(selected)}</span>
                      <span className="fd-plate" style={{ color: "#fff", background: "rgba(255,255,255,.16)", borderColor: "rgba(255,255,255,.28)" }}>
                        {selected.busId}
                      </span>
                    </div>
                  </div>
                  <div className="fd-heroactions">
                    {onOpenBus && (
                      <button
                        type="button"
                        className="fd-btn fd-btn-white fd-btn-sm"
                        onClick={() => onOpenBus(selected.busId)}
                      >
                        Open bus
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="fd-tiles">
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Odometer</span>
                  <p className="fd-tileval fd-num">{km(selected.config.odometerKm)}</p>
                  <p className="fd-tiny fd-muted" style={{ margin: "6px 0 0" }}>From the last refill with a reading</p>
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Since service</span>
                  <p className="fd-tileval fd-num">
                    {selected.service.state === "not_configured" ? "—" : km(selected.service.kmSinceService)}
                  </p>
                  <p className="fd-tiny fd-muted" style={{ margin: "6px 0 0" }}>
                    Interval {selected.config.serviceIntervalKm ? km(selected.config.serviceIntervalKm) : "not set"}
                  </p>
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Next service</span>
                  <p className="fd-tileval">{nextService}</p>
                  <p className="fd-tiny fd-muted" style={{ margin: "6px 0 0" }}>Against the interval you set</p>
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Fuel drift</span>
                  <p className="fd-tileval">
                    {selected.drift.state === "drifting" && selected.drift.driftPct != null
                      ? `${selected.drift.driftPct}% worse`
                      : <span className="fd-nodata">No drift</span>}
                  </p>
                  <p className="fd-tiny fd-muted" style={{ margin: "6px 0 0" }}>
                    {selected.drift.state === "drifting" && selected.drift.estMonthlyLossKES
                      ? `${ksh(selected.drift.estMonthlyLossKES)} / month extra`
                      : selected.drift.state === "no_baseline" || selected.drift.state === "insufficient_fills"
                        ? "Not enough fills to judge yet"
                        : "Against this bus’s own baseline"}
                  </p>
                </div>
              </div>

              <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                    <h3>Service schedule</h3>
                    <p className="fd-tiny fd-muted" style={{ margin: "0 0 14px" }}>
                      Type the kilometres on the last workshop receipt. If you do not have it, wait for a refill with an odometer reading, then start counting from that number.
                    </p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <label style={{ display: "block" }}>
                        <span className="fd-kicker" style={{ marginBottom: 6 }}>Service every (km)</span>
                        <input
                          className="fd-input"
                          type="number"
                          min={1}
                          value={form.serviceIntervalKm ?? ""}
                          onChange={(e) => setForm({ ...form, serviceIntervalKm: e.target.value })}
                          placeholder="e.g. 5000"
                          aria-invalid={kmIssue.serviceIntervalKm ? true : undefined}
                        />
                        {kmIssue.serviceIntervalKm && (
                          <p className="fd-tiny" style={{ margin: "6px 0 0", color: "var(--fd-crit-700)" }}>
                            {kmIssue.serviceIntervalKm}
                          </p>
                        )}
                      </label>
                      <label style={{ display: "block" }}>
                        <span className="fd-kicker" style={{ marginBottom: 6 }}>Odometer at last service (km)</span>
                        <input
                          className="fd-input"
                          type="number"
                          min={1}
                          value={form.lastServiceOdometerKm ?? ""}
                          onChange={(e) => setForm({ ...form, lastServiceOdometerKm: e.target.value })}
                          placeholder="e.g. 118400"
                          aria-invalid={kmIssue.lastServiceOdometerKm ? true : undefined}
                        />
                        {kmIssue.lastServiceOdometerKm && (
                          <p className="fd-tiny" style={{ margin: "6px 0 0", color: "var(--fd-crit-700)" }}>
                            {kmIssue.lastServiceOdometerKm}
                          </p>
                        )}
                        <button
                          type="button"
                          className="fd-btn fd-btn-quiet fd-btn-sm"
                          style={{ marginTop: 8 }}
                          disabled={!(selected.config.odometerKm && selected.config.odometerKm > 0)}
                          onClick={() => {
                            if (!(selected.config.odometerKm && selected.config.odometerKm > 0)) return;
                            setForm({ ...form, lastServiceOdometerKm: String(Math.round(selected.config.odometerKm)) });
                          }}
                        >
                          {selected.config.odometerKm && selected.config.odometerKm > 0
                            ? `Use latest dash reading · ${km(selected.config.odometerKm)}`
                            : "Use latest dash reading — none yet"}
                        </button>
                        <p className="fd-tiny fd-muted" style={{ margin: "6px 0 0" }}>
                          Greyed out until a driver logs a refill with the dash reading. That starts the clock from now, not from the last workshop visit.
                        </p>
                      </label>
                      {([
                        { k: "insuranceExpiry", label: "Insurance expiry" },
                        { k: "inspectionExpiry", label: "NTSA inspection expiry" },
                      ] as const).map((f) => (
                        <label key={f.k} style={{ display: "block" }}>
                          <span className="fd-kicker" style={{ marginBottom: 6 }}>{f.label}</span>
                          <input
                            ref={f.k === "insuranceExpiry" ? insuranceInputRef : inspectionInputRef}
                            className="fd-input"
                            type="date"
                            min={todayYmd()}
                            value={form[f.k] ?? ""}
                            onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                          />
                        </label>
                      ))}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
                      <button
                        type="button"
                        className="fd-btn fd-btn-primary fd-btn-sm"
                        onClick={() => void save(selected)}
                        disabled={saving || !selected.docId}
                      >
                        {saving ? "Saving…" : "Save schedule"}
                      </button>
                      {msg ? (
                        <span className="fd-tiny" style={{ color: "var(--fd-crit-700)" }}>{msg}</span>
                      ) : !saving && keptId != null && keptId === selected.docId ? (
                        <span className="fd-tiny" role="status" style={{ color: "var(--fd-ok-700)" }}>
                          {savedLine(selected)}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {selected.compliance.length > 0 && (
                    <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                      <h3>Cover</h3>
                      <div className="fd-tl">
                        {selected.compliance.map((c) => (
                          <div
                            key={c.kind}
                            className={`fd-tlrow ${c.state === "expired" ? "fd-tlrow-crit" : "fd-tlrow-warn"}`}
                          >
                            <b>{c.label}</b>
                            <span>
                              {c.state === "expired"
                                ? c.acknowledged && selected.complianceAck
                                  ? `Expired — acknowledged by ${
                                      selected.complianceAck.ackedByName || "an administrator"
                                    } on ${dayLabel(selected.complianceAck.ackedAtMs)}`
                                  : `Expired ${agoLabel(c.daysAgo)}${printedDate(c)}`
                                : `Expires ${inLabel(c.daysLeft)}${printedDate(c)}`}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 14 }}>
                        <button
                          type="button"
                          className="fd-btn fd-btn-quiet fd-btn-sm"
                          onClick={() => focusExpiryFields(selected)}
                        >
                          Renewed? Update dates
                        </button>
                        {selected.compliance.some((c) => c.state === "expired" && !c.acknowledged) && (
                          <button
                            type="button"
                            className="fd-btn fd-btn-quiet fd-btn-sm"
                            onClick={() => void setComplianceAck(selected, false)}
                            disabled={acking || !selected.docId}
                          >
                            {acking ? "Saving…" : "Acknowledge"}
                          </button>
                        )}
                        {selected.complianceAck && (
                          <button
                            type="button"
                            className="fd-btn fd-btn-quiet fd-btn-sm"
                            onClick={() => void setComplianceAck(selected, true)}
                            disabled={acking || !selected.docId}
                          >
                            {acking ? "Saving…" : "Undo"}
                          </button>
                        )}
                        {ackMsg && <span className="fd-tiny" style={{ color: "var(--fd-crit-700)" }}>{ackMsg}</span>}
                      </div>
                      {selected.compliance.some((c) => c.state === "expired") && (
                        <p className="fd-tiny fd-muted" style={{ margin: "8px 0 0" }}>
                          {selected.complianceAck
                            ? `Reminders are off until ${dayLabel(selected.complianceAck.untilMs)}, or sooner if a date changes or another document expires. The bus is still not legal to run.`
                            : "Acknowledge turns the reminders off for 30 days, or until a date changes or another document expires. It does not make the bus legal to run."}
                        </p>
                      )}
                    </div>
                  )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
